import { roundToDecimalPlaces } from '@semios/app-platform-common'
import type { TEmitterTypeValueType } from '@semios/app-platform-values-helpers-and-types'
import { enumToValueType } from '@semios/app-platform-values-helpers-and-types'
import { getSoilAWCBackgroundColor } from 'App/Map/_utils/getSoilAWCBackgroundColor'
import { getSprayConditionsTitleAndColor } from 'App/Map/_utils/getSprayConditionsTitleAndColor'
import type {
  CurrentView,
  TIrrigationFlowRate,
  TPointFlowRate,
  TValueGroupLabel,
} from 'App/Map/PanelDetails/OverviewCharts/SectionTable/types'
import type { ESprayRecommendation, TIrrigationDeviceStatus } from 'App/Map/types'
import { IconWindDirection } from 'components/icons/IconWindDirection'
import type { TColumn } from 'components/SummaryTable/SummaryTable.types'
import { translate } from 'i18n/i18n'
import { isNil } from 'lodash'
import moment from 'moment-timezone'
import { selectedValueGroupsStore } from 'stores/selectedValueGroupsStore/selectedValueGroupsStore'
import { userDetailsStore } from 'stores/userDetailsStore/userDetailsStore'
import { decamelize } from 'utils/decamelize'
import { getConvertedWaterVolume } from 'utils/getConvertedWaterVolume'
import { getInsectNameFromId } from 'utils/getInsectNameFromId'
import { mapApiEmitterTypeToLabel } from 'utils/mapApiEmitterTypeToLabel'
import { unitConverter } from 'utils/unitConverter/unitConverter'
import { IrrigationCurrentStatusBadge } from '../SummaryGridTable/_utils/by-domain/irrigationActivity/_utils/IrrigationCurrentStatusBadge/IrrigationCurrentStatusBadge'
import { BadgeCell } from '../SummaryGridTable/BadgeCell/BadgeCell'
import { EllipsisTooltip } from './EllipsisTooltip'
import { groupedKeys } from './OverviewCharts'
import { basicSort, camelToKebab, pestSorter } from './SectionTable/utils'
import type { emitterList } from './valueGroupEnums'

const irrigationKeys = [
  'irrigationActivity_todaySoFarSum',
  'irrigationActivity_previous7DaysSum',
  'irrigationActivity_previous30DaysSum',
  'irrigationActivity_sinceJan1Sum',
  'irrigationActivity_sinceApr1Sum',
  'irrigationActivity_sinceJul1Sum',
  'irrigationActivity_sinceOct1Sum',
]

type TInsectTrapCatches =
  | 'insectTrapCatches_todaySum'
  | 'insectTrapCatches_previous2DaysSum'
  | 'insectTrapCatches_previous3DaysSum'
  | 'insectTrapCatches_previous7DaysSum'
  | 'insectTrapCatches_previous14DaysSum'
  | 'insectTrapCatches_previous30DaysSum'
  | 'insectTrapCatches_previous60DaysSum'
  | 'insectTrapCatches_sinceBiofixSum'
  | 'insectTrapCatches_sinceJan1Sum'
  | 'insectTrapCatches_sinceJul1Sum'

type TInsectDegreeDays =
  | 'insectDegreeDays_sinceBiofixSumMinMedianMax'
  | 'insectDegreeDays7DaysFromNow_sinceBiofixSumMinMedianMax'
  | 'insectDegreeDays14DaysFromNow_sinceBiofixSumMinMedianMax'

type TInsectTrapCatchesKey = `${TInsectTrapCatches}_insectId${number}`

type TInsectDegreeDaysKey = `${TInsectDegreeDays}_insectId${number}_degreeDaysC`

export type CurrentValuesDataInfo = {
  'pointName'?: string
  'blockName'?: string
  'type'?: string
  'crop'?: string
  'biofix'?: string
  'timezone': string
  'insectId'?: string
  'currentDD'?: {
    low: number | null
    high: number | null
  }
  '7dayDD'?: {
    low: number | null
    high: number | null
  }
  '14dayDD'?: {
    low: number | null
    high: number | null
  }
  'windSpeed_mps'?: number
  'windDirection_degrees'?: number
  'fav_depths'?: string[]
  'almondHullSplit_date'?: string
  'temperatureInversion_aboveVsIn_c'?: number
  'device'?: string
  [key: TInsectTrapCatchesKey]: number
  [key: TInsectDegreeDaysKey]: number
} & {
  [emitter in (typeof emitterList)[number] as `irrigationIsOn_${emitter}`]?: string
} & {
  [emitter in (typeof irrigationKeys)[number] as `${emitter}_${(typeof emitterList)[number]}_ms`]?: number
}

export type OverviewColumnsParams = {
  view: string | null
  selectedValueGroupsArray: TValueGroupLabel[]
  currentView: CurrentView | null
  insectIds: number[] | null
  pointsFlowRates: TPointFlowRate[] | null
  timestamp: string | null
  isSouthernHemisphere: boolean
  propertyId: number
}

export const OverviewColumns = ({
  view,
  selectedValueGroupsArray,
  currentView,
  insectIds,
  pointsFlowRates,
  timestamp,
  isSouthernHemisphere,
  propertyId,
}: OverviewColumnsParams) => {
  const cultivar = translate.phrases.banyanApp('Nonpareil')
  const isImperial = userDetailsStore.getState().rain === 'IMPERIAL'

  const getFlowRateForDevice = (pointFlowRates: TIrrigationFlowRate[] | [], device: string): number => {
    if (!device) return 0

    const kebabDevice = camelToKebab(device)

    const found = pointFlowRates.find(
      ({ irrigationZone }) => typeof irrigationZone === 'string' && irrigationZone.endsWith(kebabDevice),
    )

    return found?.flowRate ? Number(found.flowRate) : 0
  }

  const renderAppliedAmount = (
    record: CurrentValuesDataInfo,
    irrigationKey: string,
    currentView: CurrentView | null,
    pointsFlowRates:
      | { pointName: string; propertyId: number; flowRates: TIrrigationFlowRate[] | [] }[]
      | null,
  ) => {
    const MILLISECONDS_IN_HOUR = 3600000
    const valueAsNumber = Number(record[irrigationKey as keyof CurrentValuesDataInfo])
    const durationHours = valueAsNumber / MILLISECONDS_IN_HOUR

    const flowRates =
      pointsFlowRates?.find(
        (p: TPointFlowRate) =>
          p.pointName === record?.pointName && Number(p.propertyId) === Number(propertyId),
      )?.flowRates || []

    const device = record.device as string
    const flowRateMillimetresPerHour = getFlowRateForDevice(flowRates, device)

    if (currentView?.irrigation === 'DURATION') {
      const durationHoursRounded = Number(roundToDecimalPlaces(durationHours, 1))

      return `${translate.measurements.hours.valueWithUnit(durationHoursRounded, 1)}`
    } else if (currentView?.irrigation === 'VOLUME') {
      const millimetresApplied = flowRateMillimetresPerHour * durationHours
      const imperialUnit = isImperial ? 'INCH' : 'MILLIMETER'
      const decimalPlaces = isImperial ? 2 : 1

      const appliedAmountFinalUnit =
        getConvertedWaterVolume({
          waterApplied: millimetresApplied,
          volumeUnitToDisplay: imperialUnit,
          flowUnitPerHour: imperialUnit,
          decimalPlacesToRound: decimalPlaces,
        }) || 0

      return unitConverter.irrigationActivity(appliedAmountFinalUnit).valueWithSuffix()
    }
  }

  const getTimestampColumn = (timestamp: string) => ({
    title: <EllipsisTooltip fullText={translate.phrases.banyanApp('Last Fetched')} />,
    dataIndex: 'lastFetched',
    key: 'lastFetched',
    fixed: 'right',
    width: 200,
    render: () => {
      const timezone = moment.tz.guess()
      const momentTime = moment.tz(timestamp, timezone)
      const timeFormat = translate.dates.format(momentTime, 'h:mm a')
      const languageLabel = translate.dates.format(momentTime, 'ddd, MMM D')

      return `${languageLabel}, ${timeFormat}`
    },
  })

  const selectedValueGroups = selectedValueGroupsStore.getState().selectedValueGroups

  const getStationsColumn = (view: string | null) => ({
    title: <EllipsisTooltip fullText={translate.phrases.banyanApp('Stations')} />,
    dataIndex: 'pointName',
    key: `pointName_${view}`,
    width: 250,
    fixed: 'left',
    render: (value: string) => {
      return <EllipsisTooltip fullText={value} />
    },
  })

  const getBlocksColumn = (view: string | null) => ({
    title: <EllipsisTooltip fullText={translate.phrases.banyanApp('Blocks')} />,
    dataIndex: 'blockName',
    key: `blockName_${view}`,
    width: 250,
    fixed: 'left',
    render: (value: string) => {
      return <EllipsisTooltip fullText={value} />
    },
  })

  const columns: {
    [layer: string]: {
      [category: string]: TColumn[]
    }
  } = {
    environment: {
      air_temperature: [
        getStationsColumn(view),
        {
          title: <EllipsisTooltip fullText={translate.phrases.banyanApp('Temperature')} />,
          dataIndex: 'temperature_in_c',
          key: 'temperature_in_c',
          width: 150,
          render: (value: number) =>
            isNil(value)
              ? translate.phrases.templates('-')
              : unitConverter.temperatureInCanopy(value).valueWithSuffix(),
        },
        {
          title: <EllipsisTooltip fullText={translate.phrases.banyanApp('Temperature Inversion')} />,
          dataIndex: 'temperatureInversion_aboveVsIn_c',
          key: 'temperatureInversion_aboveVsIn_c',
          width: 200,
          render: (value: number) =>
            isNil(value)
              ? translate.phrases.templates('-')
              : unitConverter.temperatureInversion(value).valueWithSuffix(),
        },
      ],
      wind: [
        getStationsColumn(view),
        {
          title: <EllipsisTooltip fullText={translate.phrases.banyanApp('Wind')} />,
          dataIndex: 'windSpeed_mps',
          key: 'windSpeed_mps',
          width: 150,
          render: (_: number, record: CurrentValuesDataInfo) => {
            if (isNil(record.windSpeed_mps)) {
              return translate.phrases.templates('-')
            }

            return (
              <>
                {unitConverter.windSpeed(record.windSpeed_mps).valueWithSuffix()}
                <IconWindDirection windDirection={record.windDirection_degrees} />
              </>
            )
          },
        },
      ],
      humidity: [
        getStationsColumn(view),
        {
          title: <EllipsisTooltip fullText={translate.phrases.banyanApp('Humidity')} />,
          dataIndex: 'humidity_in_pct',
          key: 'humidity_in_pct',
          width: 200,
          render: (value: number) =>
            isNil(value)
              ? translate.phrases.templates('-')
              : unitConverter.humidityInCanopy(value).valueWithSuffix(),
        },
      ],
      leaf_wetness: [
        getStationsColumn(view),
        {
          title: (
            <EllipsisTooltip
              fullText={translate.phrases.templates('{{labelA}} ({{labelB}})', {
                labelA: translate.phrases.banyanApp('Wet Hours'),
                labelB: translate.phrases.banyanApp('Last {{count}} Hours', { count: 24 }),
              })}
            />
          ),
          dataIndex: 'leafWetness_last24HoursSum_wetHours',
          key: 'leafWetness_last24HoursSum_wetHours',
          width: 225,
          render: (value: number) =>
            isNil(value)
              ? translate.phrases.templates('-')
              : unitConverter.leafWetnessHours(value).valueWithSuffix(),
        },
        {
          title: (
            <EllipsisTooltip
              fullText={translate.phrases.templates('{{labelA}} ({{labelB}})', {
                labelA: translate.phrases.banyanApp('Wet Hours'),
                labelB: translate.phrases.banyanApp('Next {{count}} Hours', { count: 24 }),
              })}
            />
          ),
          dataIndex: 'leafWetness_next24HoursSum_wetHours',
          key: 'leafWetness_next24HoursSum_wetHours',
          width: 225,
          render: (value: number) =>
            isNil(value)
              ? translate.phrases.templates('-')
              : unitConverter.leafWetnessHours(value).valueWithSuffix(),
        },
      ],
      precipitation: [
        getStationsColumn(view),
        // {
        //   title:  <EllipsisTooltip fullText={translate.phrases.templates('{{labelA}} ({{labelB}})', {
        //     labelA: translate.phrases.banyanApp('Rain'),
        //     labelB: translate.phrases.banyanApp('Today'),
        //   })} />,
        //   dataIndex: 'precipitation_todayTotalSum_mm',
        //   key: 'precipitation_todayTotalSum_mm',
        //   width: 150,
        //   render: (value: number) => {
        //     if (value) return `${unitConverter.precipitation(value).valueWithSuffix()}`

        //     return translate.phrases.templates('-')
        //   },
        // },
        {
          title: (
            <EllipsisTooltip
              fullText={translate.phrases.templates('{{labelA}} ({{labelB}})', {
                labelA: translate.phrases.banyanApp('Rain'),
                labelB: translate.phrases.banyanApp('Next {{count}} Days', { count: 7 }),
              })}
            />
          ),
          dataIndex: 'precipitation_following7DaysSum_mm',
          key: 'precipitation_following7DaysSum_mm',
          width: 175,
          render: (value: number) =>
            isNil(value)
              ? translate.phrases.templates('-')
              : unitConverter.precipitation(value).valueWithSuffix(),
        },
        {
          title: (
            <EllipsisTooltip
              fullText={translate.phrases.templates('{{labelA}} ({{labelB}})', {
                labelA: translate.phrases.banyanApp('Rain'),
                labelB: translate.phrases.banyanApp('Last {{count}} Hours', { count: 24 }),
              })}
            />
          ),
          dataIndex: 'precipitation_last24HoursSum_mm',
          key: 'precipitation_last24HoursSum_mm',
          width: 175,
          render: (value: number) =>
            isNil(value)
              ? translate.phrases.templates('-')
              : unitConverter.precipitation(value).valueWithSuffix(),
        },
        {
          title: (
            <EllipsisTooltip
              fullText={translate.phrases.templates('{{labelA}} ({{labelB}})', {
                labelA: translate.phrases.banyanApp('Rain'),
                labelB: translate.phrases.banyanApp('Next {{count}} Hours', { count: 24 }),
              })}
            />
          ),
          dataIndex: 'precipitation_next24HoursSum_mm',
          key: 'precipitation_next24HoursSum_mm',
          width: 175,
          render: (value: number) =>
            isNil(value)
              ? translate.phrases.templates('-')
              : unitConverter.precipitation(value).valueWithSuffix(),
        },
        {
          title: (
            <EllipsisTooltip
              fullText={translate.phrases.templates('{{labelA}} ({{labelB}})', {
                labelA: translate.phrases.banyanApp('Rain'),
                labelB: translate.phrases.banyanApp('Last {{count}} Days', { count: 7 }),
              })}
            />
          ),
          dataIndex: 'precipitation_previous7DaysSum_mm',
          key: 'precipitation_previous7DaysSum_mm',
          width: 175,
          render: (value: number) =>
            isNil(value)
              ? translate.phrases.templates('-')
              : unitConverter.precipitation(value).valueWithSuffix(),
        },
        ...(!isSouthernHemisphere
          ? [
              {
                title: (
                  <EllipsisTooltip
                    fullText={translate.phrases.templates('{{labelA}} ({{labelB}})', {
                      labelA: translate.phrases.banyanApp('Rain'),
                      labelB: translate.phrases.banyanApp('Since {{date}}', {
                        date: translate.dates.format(moment.tz('UTC').month(0).date(1), 'MMM D'), // Jan 1
                      }),
                    })}
                  />
                ),
                dataIndex: 'precipitation_sinceJan1Sum_mm',
                key: 'precipitation_sinceJan1Sum_mm',
                width: 175,
                render: (value: number) =>
                  isNil(value)
                    ? translate.phrases.templates('-')
                    : unitConverter.precipitation(value).valueWithSuffix(),
              },
              {
                title: (
                  <EllipsisTooltip
                    fullText={translate.phrases.templates('{{labelA}} ({{labelB}})', {
                      labelA: translate.phrases.banyanApp('Rain'),
                      labelB: translate.phrases.banyanApp('Since {{date}}', {
                        date: translate.dates.format(moment.tz('UTC').month(9).date(1), 'MMM D'), // Oct 1
                      }),
                    })}
                  />
                ),
                dataIndex: 'precipitation_sinceOct1Sum_mm',
                key: 'precipitation_sinceOct1Sum_mm',
                width: 175,
                render: (value: number) =>
                  isNil(value)
                    ? translate.phrases.templates('-')
                    : unitConverter.precipitation(value).valueWithSuffix(),
              },
            ]
          : []),
        ...(isSouthernHemisphere
          ? [
              {
                title: (
                  <EllipsisTooltip
                    fullText={translate.phrases.templates('{{labelA}} ({{labelB}})', {
                      labelA: translate.phrases.banyanApp('Rain'),
                      labelB: translate.phrases.banyanApp('Since {{date}}', {
                        date: translate.dates.format(moment.tz('UTC').month(3).date(1), 'MMM D'), // Apr 1
                      }),
                    })}
                  />
                ),
                dataIndex: 'precipitation_sinceApr1Sum_mm',
                key: 'precipitation_sinceApr1Sum_mm',
                width: 175,
                render: (value: number) =>
                  isNil(value)
                    ? translate.phrases.templates('-')
                    : unitConverter.precipitation(value).valueWithSuffix(),
              },
              {
                title: (
                  <EllipsisTooltip
                    fullText={translate.phrases.templates('{{labelA}} ({{labelB}})', {
                      labelA: translate.phrases.banyanApp('Rain'),
                      labelB: translate.phrases.banyanApp('Since {{date}}', {
                        date: translate.dates.format(moment.tz('UTC').month(6).date(1), 'MMM D'), // Jul 1
                      }),
                    })}
                  />
                ),
                dataIndex: 'precipitation_sinceJul1Sum_mm',
                key: 'precipitation_sinceJul1Sum_mm',
                width: 175,
                render: (value: number) =>
                  isNil(value)
                    ? translate.phrases.templates('-')
                    : unitConverter.precipitation(value).valueWithSuffix(),
              },
            ]
          : []),
      ],
      dew_point: [
        getStationsColumn(view),
        {
          title: <EllipsisTooltip fullText={translate.phrases.banyanApp('Dew Point')} />,
          dataIndex: 'dewPoint_in_c',
          key: 'dewPoint_in_c',
          width: 200,
          render: (value: number) =>
            isNil(value)
              ? translate.phrases.templates('-')
              : unitConverter.dewPointInCanopy(value).valueWithSuffix(),
        },
      ],
      wet_bulb: [
        getStationsColumn(view),
        {
          title: <EllipsisTooltip fullText={translate.phrases.banyanApp('Wet Bulb')} />,
          dataIndex: 'wetBulb_in_c',
          key: 'wetBulb_in_c',
          width: 200,
          render: (value: number) =>
            isNil(value)
              ? translate.phrases.templates('-')
              : unitConverter.wetBulbInCanopy(value).valueWithSuffix(),
        },
      ],
    },
    waterManagement: {
      // NOTE: Not yet supported
      // irrigation_summary: [
      //   {
      //     title:  <EllipsisTooltip fullText={translate.phrases.banyanApp('Rain (last 7 days)'),
      //     dataIndex: 'precipitation_previous7DaysSum_mm',
      //     key: 'precipitation_previous7DaysSum_mm',
      //     render: (value: number, record: CurrentValuesDataInfo) => {
      //       if (value) return `${unitConverter.precipitation(value).valueWithSuffix()}`
      //       return translate.phrases.templates('-')
      //     },
      //   },
      //   // Irrigation columns only if irrigation_activity selected
      //   ...(selectedValueGroups.irrigation_activity
      //     ? [
      //         {
      //           title:  <EllipsisTooltip fullText={translate.phrases.banyanApp('Irrigation (current)'),
      //           dataIndex: 'irrigationIsOn_status',
      //           key: 'irrigationIsOn_status',
      //         },
      //         {
      //           title:  <EllipsisTooltip fullText={translate.phrases.banyanApp('Irrigation (last 7 days)'),
      //           dataIndex: 'irrigationActivity_previous7DaysSum_equipment_ms_summary',
      //           key: 'irrigationActivity_previous7DaysSum_equipment_ms_summary',
      //           render: (value: number, record: CurrentValuesDataInfo) => {
      //             const previous7DaysSum: any = Object.keys(record).find((key) =>
      //               key.startsWith('irrigationActivity_previous7DaysSum'),
      //             )
      //             if (previous7DaysSum) return record[previous7DaysSum]
      //             return translate.phrases.templates('-')
      //           },
      //         },
      //       ]
      //     : []),
      //   // ET columns only if evapotranspiration selected
      //   ...(selectedValueGroups.evapotranspiration
      //     ? [
      //         {
      //           title:  <EllipsisTooltip fullText={translate.phrases.banyanApp('ETc (next week)'),
      //           dataIndex: 'evapotranspirationETc_next7DaysSum_mm',
      //           key: 'evapotranspirationETc_next7DaysSum_mm',
      //           render: (value: number) => {
      //             if (value) return `${unitConverter.evapotranspiration(value).valueWithSuffix()}`
      //             return translate.phrases.templates('-')
      //           },
      //         },
      //         {
      //           title:  <EllipsisTooltip fullText={translate.phrases.banyanApp('ETc (last week)'),
      //           dataIndex: 'evapotranspirationETc_previous7DaysSum_mm',
      //           key: 'evapotranspirationETc_previous7DaysSum_mm',
      //           render: (value: number) => {
      //             if (value) return `${unitConverter.evapotranspiration(value).valueWithSuffix()}`
      //             return translate.phrases.templates('-')
      //           },
      //         },
      //         {
      //           title:  <EllipsisTooltip fullText={translate.phrases.banyanApp('ETo (next week)'),
      //           dataIndex: 'evapotranspirationETo_next7DaysSum_mm',
      //           key: 'evapotranspirationETo_next7DaysSum_mm',
      //           render: (value: number) => {
      //             if (value) return `${unitConverter.evapotranspiration(value).valueWithSuffix()}`
      //             return translate.phrases.templates('-')
      //           },
      //         },
      //         {
      //           title:  <EllipsisTooltip fullText={translate.phrases.banyanApp('ETo (last week)'),
      //           dataIndex: 'evapotranspirationETo_previous7DaysSum_mm',
      //           key: 'evapotranspirationETo_previous7DaysSum_mm',
      //           render: (value: number) => {
      //             if (value) return `${unitConverter.evapotranspiration(value).valueWithSuffix()}`
      //             return translate.phrases.templates('-')
      //           },
      //         },
      //       ]
      //     : []),
      // ],
      evapotranspiration: [
        getBlocksColumn(view),
        {
          title: <EllipsisTooltip fullText={translate.phrases.banyanApp('Blocks')} />,
          dataIndex: 'blockName',
          key: `blockName_${view}`,
          width: 250,
          fixed: 'left',
          render: (value: string) => {
            return <EllipsisTooltip fullText={value} />
          },
        },
        {
          title: <EllipsisTooltip fullText={translate.phrases.banyanApp('Crop')} />,
          dataIndex: 'crop',
          key: 'crop',
          width: 150,
          render: (value: string) => value, // TODO
        },
        {
          title: (
            <EllipsisTooltip
              fullText={translate.phrases.templates('{{labelA}} ({{labelB}})', {
                labelA: translate.phrases.unitSymbols('ETo'),
                labelB: translate.phrases.banyanApp('Last {{count}} Days', { count: 30 }),
              })}
            />
          ),
          dataIndex: 'evapotranspirationETo_previous30DaysSum_mm',
          key: 'evapotranspirationETo_previous30DaysSum_mm',
          width: 170,
          render: (value: number) =>
            isNil(value)
              ? translate.phrases.templates('-')
              : unitConverter.evapotranspiration(value).valueWithSuffix(),
        },
        {
          title: (
            <EllipsisTooltip
              fullText={translate.phrases.templates('{{labelA}} ({{labelB}})', {
                labelA: translate.phrases.unitSymbols('ETo'),
                labelB: translate.phrases.banyanApp('Last {{count}} Days', { count: 14 }),
              })}
            />
          ),
          dataIndex: 'evapotranspirationETo_previous14DaysSum_mm',
          key: 'evapotranspirationETo_previous14DaysSum_mm',
          width: 170,
          render: (value: number) =>
            isNil(value)
              ? translate.phrases.templates('-')
              : unitConverter.evapotranspiration(value).valueWithSuffix(),
        },

        {
          title: (
            <EllipsisTooltip
              fullText={translate.phrases.templates('{{labelA}} ({{labelB}})', {
                labelA: translate.phrases.unitSymbols('ETo'),
                labelB: translate.phrases.banyanApp('Last {{count}} Days', { count: 7 }),
              })}
            />
          ),
          dataIndex: 'evapotranspirationETo_previous7DaysSum_mm',
          key: 'evapotranspirationETo_previous7DaysSum_mm',
          width: 170,
          render: (value: number) =>
            isNil(value)
              ? translate.phrases.templates('-')
              : unitConverter.evapotranspiration(value).valueWithSuffix(),
        },
        {
          title: (
            <EllipsisTooltip
              fullText={translate.phrases.templates('{{labelA}} ({{labelB}})', {
                labelA: translate.phrases.unitSymbols('ETo'),
                labelB: translate.phrases.banyanApp('Next {{count}} Days', { count: 7 }),
              })}
            />
          ),
          dataIndex: 'evapotranspirationETo_next7DaysSum_mm',
          key: 'evapotranspirationETo_next7DaysSum_mm',
          width: 170,
          render: (value: number) =>
            isNil(value)
              ? translate.phrases.templates('-')
              : unitConverter.evapotranspiration(value).valueWithSuffix(),
        },
        {
          title: (
            <EllipsisTooltip
              fullText={translate.phrases.templates('{{labelA}} ({{labelB}})', {
                labelA: translate.phrases.unitSymbols('ETo'),
                labelB: translate.phrases.banyanApp('Next {{count}} Days', { count: 14 }),
              })}
            />
          ),
          dataIndex: 'evapotranspirationETo_next14DaysSum_mm',
          key: 'evapotranspirationETo_next14DaysSum_mm',
          width: 170,
          render: (value: number) =>
            isNil(value)
              ? translate.phrases.templates('-')
              : unitConverter.evapotranspiration(value).valueWithSuffix(),
        },
        {
          title: (
            <EllipsisTooltip
              fullText={translate.phrases.templates('{{labelA}} ({{labelB}})', {
                labelA: translate.phrases.unitSymbols('ETc'),
                labelB: translate.phrases.banyanApp('Last {{count}} Days', { count: 30 }),
              })}
            />
          ),
          dataIndex: 'evapotranspirationETc_previous30DaysSum_mm',
          key: 'evapotranspirationETc_previous30DaysSum_mm',
          width: 170,
          render: (value: number) =>
            isNil(value)
              ? translate.phrases.templates('-')
              : unitConverter.evapotranspiration(value).valueWithSuffix(),
        },
        {
          title: (
            <EllipsisTooltip
              fullText={translate.phrases.templates('{{labelA}} ({{labelB}})', {
                labelA: translate.phrases.unitSymbols('ETc'),
                labelB: translate.phrases.banyanApp('Last {{count}} Days', { count: 14 }),
              })}
            />
          ),
          dataIndex: 'evapotranspirationETc_previous14DaysSum_mm',
          key: 'evapotranspirationETc_previous14DaysSum_mm',
          width: 170,
          render: (value: number) =>
            isNil(value)
              ? translate.phrases.templates('-')
              : unitConverter.evapotranspiration(value).valueWithSuffix(),
        },
        {
          title: (
            <EllipsisTooltip
              fullText={translate.phrases.templates('{{labelA}} ({{labelB}})', {
                labelA: translate.phrases.unitSymbols('ETc'),
                labelB: translate.phrases.banyanApp('Last {{count}} Days', { count: 7 }),
              })}
            />
          ),
          dataIndex: 'evapotranspirationETc_previous7DaysSum_mm',
          key: 'evapotranspirationETc_previous7DaysSum_mm',
          width: 170,
          render: (value: number) =>
            isNil(value)
              ? translate.phrases.templates('-')
              : unitConverter.evapotranspiration(value).valueWithSuffix(),
        },
        {
          title: (
            <EllipsisTooltip
              fullText={translate.phrases.templates('{{labelA}} ({{labelB}})', {
                labelA: translate.phrases.unitSymbols('ETc'),
                labelB: translate.phrases.banyanApp('Next {{count}} Days', { count: 7 }),
              })}
            />
          ),
          dataIndex: 'evapotranspirationETc_next7DaysSum_mm',
          key: 'evapotranspirationETc_next7DaysSum_mm',
          width: 170,
          render: (value: number) =>
            isNil(value)
              ? translate.phrases.templates('-')
              : unitConverter.evapotranspiration(value).valueWithSuffix(),
        },
        {
          title: (
            <EllipsisTooltip
              fullText={translate.phrases.templates('{{labelA}} ({{labelB}})', {
                labelA: translate.phrases.unitSymbols('ETc'),
                labelB: translate.phrases.banyanApp('Next {{count}} Days', { count: 14 }),
              })}
            />
          ),
          dataIndex: 'evapotranspirationETc_next14DaysSum_mm',
          key: 'evapotranspirationETc_next14DaysSum_mm',
          width: 170,
          render: (value: number) =>
            isNil(value)
              ? translate.phrases.templates('-')
              : unitConverter.evapotranspiration(value).valueWithSuffix(),
        },
      ],
      fruit_growth: [
        getStationsColumn(view),
        {
          title: <EllipsisTooltip fullText={translate.phrases.banyanApp('Stations')} />,
          dataIndex: 'pointName',
          key: `pointName_${view}`,
          width: 250,
          fixed: 'left',
          render: (value: string) => {
            return <EllipsisTooltip fullText={value} />
          },
        },
        {
          title: <EllipsisTooltip fullText={translate.phrases.banyanApp('Fruit Size')} />,
          dataIndex: 'fruitSize_um',
          key: 'fruitSize_um',
          width: 150,
          render: (value: number) =>
            isNil(value)
              ? translate.phrases.templates('-')
              : unitConverter.fruitGrowth(value).valueWithSuffix(),
        },
        {
          title: (
            <EllipsisTooltip
              fullText={translate.phrases.templates('{{labelA}} ({{labelB}})', {
                labelA: translate.phrases.banyanApp('Fruit Growth'),
                labelB: translate.phrases.banyanApp('Last {{count}} Days', { count: 7 }),
              })}
            />
          ),
          dataIndex: 'fruitGrowth_last7DaysSum_um',
          key: 'fruitGrowth_last7DaysSum_um',
          width: 230,
          render: (value: number) =>
            isNil(value)
              ? translate.phrases.templates('-')
              : unitConverter.fruitGrowth(value).valueWithSuffix(),
        },
        {
          title: (
            <EllipsisTooltip
              fullText={translate.phrases.templates('{{labelA}} ({{labelB}})', {
                labelA: translate.phrases.banyanApp('Fruit Growth'),
                labelB: translate.phrases.banyanApp('Last {{count}} Days', { count: 14 }),
              })}
            />
          ),
          dataIndex: 'fruitGrowth_last14DaysSum_um',
          key: 'fruitGrowth_last14DaysSum_um',
          width: 230,
          render: (value: number) =>
            isNil(value)
              ? translate.phrases.templates('-')
              : unitConverter.fruitGrowth(value).valueWithSuffix(),
        },
        {
          title: (
            <EllipsisTooltip
              fullText={translate.phrases.templates('{{labelA}} ({{labelB}})', {
                labelA: translate.phrases.banyanApp('Fruit Growth'),
                labelB: translate.phrases.banyanApp('Last {{count}} Days', { count: 30 }),
              })}
            />
          ),
          dataIndex: 'fruitGrowth_last30DaysSum_um',
          key: 'fruitGrowth_last30DaysSum_um',
          width: 230,
          render: (value: number) =>
            isNil(value)
              ? translate.phrases.templates('-')
              : unitConverter.fruitGrowth(value).valueWithSuffix(),
        },
        {
          title: (
            <EllipsisTooltip
              fullText={translate.phrases.templates('{{labelA}} ({{labelB}})', {
                labelA: translate.phrases.banyanApp('Fruit Growth'),
                labelB: translate.phrases.banyanApp('Next {{count}} Days', { count: 14 }),
              })}
            />
          ),
          dataIndex: 'fruitGrowth_next14DaysSum_um',
          key: 'fruitGrowth_next14DaysSum_um',
          width: 230,
          render: (value: number) =>
            isNil(value)
              ? translate.phrases.templates('-')
              : unitConverter.fruitGrowth(value).valueWithSuffix(),
        },
        {
          title: (
            <EllipsisTooltip
              fullText={translate.phrases.templates('{{labelA}} ({{labelB}})', {
                labelA: translate.phrases.banyanApp('Fruit Growth Target'),
                labelB: translate.phrases.banyanApp('Next {{count}} Days', { count: 14 }),
              })}
            />
          ),
          dataIndex: 'fruitGrowthTarget_next14DaysSum_um',
          key: 'fruitGrowthTarget_next14DaysSum_um',
          width: 280,
          render: (value: number) =>
            isNil(value)
              ? translate.phrases.templates('-')
              : unitConverter.fruitGrowth(value).valueWithSuffix(),
        },
      ],
      irrigation_activity: [
        {
          title: <EllipsisTooltip fullText={translate.phrases.banyanApp('Device')} />,
          dataIndex: 'device',
          key: 'device',
          width: 350,
          render: (value: TEmitterTypeValueType, record: CurrentValuesDataInfo) => {
            if (value) {
              const emitterTypeEnum = enumToValueType[value]
              const deviceName = mapApiEmitterTypeToLabel(emitterTypeEnum)
              const fullText = `${record.pointName} (${decamelize(deviceName)})`

              return <EllipsisTooltip fullText={fullText} />
            }

            return translate.phrases.banyanApp('Unnamed Device')
          },
          sorter: (a: CurrentValuesDataInfo, b: CurrentValuesDataInfo) => {
            const getLabel = (record: CurrentValuesDataInfo) => {
              const valueWithSuffix = '_ms'
              const matchingKey = Object.keys(record).find((key) => key.endsWith(valueWithSuffix))

              if (matchingKey) {
                const valueType = (matchingKey.split('_ms')[0].split('_').pop() ||
                  '') as TEmitterTypeValueType

                const emitterTypeEnum = enumToValueType[valueType]
                const deviceName = mapApiEmitterTypeToLabel(emitterTypeEnum)

                return `${record.pointName} (${decamelize(deviceName)})`
              }

              return null
            }

            const aLabel = getLabel(a)
            const bLabel = getLabel(b)

            if (aLabel === null && bLabel === null) return 0

            if (aLabel === null) return 1

            if (bLabel === null) return -1

            return aLabel.localeCompare(bLabel, undefined, { numeric: true, sensitivity: 'base' })
          },
        },
        {
          title: <EllipsisTooltip fullText={translate.phrases.banyanApp('Status')} />,
          dataIndex: 'irrigationIsOn_equipment_status',
          key: 'irrigationIsOn_equipment_status',
          width: 150,
          render: (_: string, record: CurrentValuesDataInfo) => {
            const status = Object.keys(record).find((key) => key.startsWith('irrigationIsOn_'))

            let currentStatus = record[status as keyof CurrentValuesDataInfo] as TIrrigationDeviceStatus

            if (!status || currentStatus === null) currentStatus = 'unknown'

            return (
              <div
                css={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  fontSize: '12px',
                  width: 'fit-content',
                }}
              >
                <IrrigationCurrentStatusBadge currentValueStatus={currentStatus} />
              </div>
            )
          },
        },
        {
          title: (
            <EllipsisTooltip
              fullText={translate.phrases.templates('{{labelA}} ({{labelB}})', {
                labelA: translate.phrases.banyanApp('Applied'),
                labelB: translate.phrases.banyanApp('Today'),
              })}
            />
          ),
          dataIndex: 'irrigationActivity_todaySoFarSum_equipment_ms',
          key: 'irrigationActivity_todaySoFarSum_equipment_ms',
          width: 150,
          render: (_: number, record: CurrentValuesDataInfo) => {
            const todaySoFarSum = Object.keys(record).find((key) =>
              key.startsWith('irrigationActivity_todaySoFarSum'),
            )

            if (todaySoFarSum) {
              return renderAppliedAmount(record, todaySoFarSum, currentView, pointsFlowRates)
            }

            return translate.phrases.templates('-')
          },
        },
        {
          title: (
            <EllipsisTooltip
              fullText={translate.phrases.templates('{{labelA}} ({{labelB}})', {
                labelA: translate.phrases.banyanApp('Applied'),
                labelB: translate.phrases.banyanApp('Last {{count}} Days', { count: 7 }),
              })}
            />
          ),
          dataIndex: 'irrigationActivity_previous7DaysSum_equipment_ms',
          key: 'irrigationActivity_previous7DaysSum_equipment_ms',
          width: 200,
          render: (_: number, record: CurrentValuesDataInfo) => {
            const previous7DaysSumKey = Object.keys(record).find((key) =>
              key.startsWith('irrigationActivity_previous7DaysSum'),
            )

            if (previous7DaysSumKey) {
              return renderAppliedAmount(record, previous7DaysSumKey, currentView, pointsFlowRates)
            }

            return translate.phrases.templates('-')
          },
        },
        {
          title: (
            <EllipsisTooltip
              fullText={translate.phrases.templates('{{labelA}} ({{labelB}})', {
                labelA: translate.phrases.banyanApp('Applied'),
                labelB: translate.phrases.banyanApp('Last {{count}} Days', { count: 30 }),
              })}
            />
          ),
          dataIndex: 'irrigationActivity_previous30DaysSum_equipment_ms',
          key: 'irrigationActivity_previous30DaysSum_equipment_ms',
          width: 200,
          render: (_: number, record: CurrentValuesDataInfo) => {
            const previous30DaysSumKey = Object.keys(record).find((key) =>
              key.startsWith('irrigationActivity_previous30DaysSum'),
            )

            if (!isNil(previous30DaysSumKey)) {
              return renderAppliedAmount(record, previous30DaysSumKey, currentView, pointsFlowRates)
            }

            return translate.phrases.templates('-')
          },
        },
        // {
        //   title:  <EllipsisTooltip fullText={ translate.phrases.banyanApp('Cumulative Flow Today'),
        //   dataIndex: 'irrigationActivity_today_cumulativeFlowMeter_gallons',
        //   key: 'irrigationActivity_today_cumulativeFlowMeter_gallons',
        //   width: 200,
        //   render: (value: number, record: any) => {
        //     if (value) return `${unitConverter.flowMeter(value).valueWithSuffix()}`

        //     return translate.phrases.templates('-')
        //   },
        // },
        // {
        //   title:  <EllipsisTooltip fullText={ translate.phrases.templates('{{labelA}} ({{labelB}})', {
        //     labelA: translate.phrases.banyanApp('Cumulative Flow'),
        //     labelB: translate.phrases.banyanApp('Prev {{count}} Days', { count: 7 }),
        //   }),
        //   dataIndex: 'irrigationActivity_prev7Days_cumulativeFlowMeter_gallons',
        //   key: 'irrigationActivity_prev7Days_cumulativeFlowMeter_gallons',
        //   width: 250,
        //   render: (value: number) => {
        //     if (value) return `${unitConverter.flowMeter(value).valueWithSuffix()}`

        //     return translate.phrases.templates('-')
        //   },
        // },
        // {
        //   title:  <EllipsisTooltip fullText={ translate.phrases.templates('{{labelA}} ({{labelB}})', {
        //     labelA: translate.phrases.banyanApp('Cumulative Flow'),
        //     labelB: translate.phrases.banyanApp('Season'),
        //   }),
        //   dataIndex: 'irrigationActivity_season_cumulativeFlowMeter_gallons',
        //   key: 'irrigationActivity_season_cumulativeFlowMeter_gallons',
        //   width: 220,
        //   render: (value: number) => {
        //     if (value) return `${unitConverter.flowMeter(value).valueWithSuffix()}`

        //     return translate.phrases.templates('-')
        //   },
        // },
        // {
        //   title:  <EllipsisTooltip fullText={ translate.phrases.templates('{{labelA}} ({{labelB}})', {
        //     labelA: translate.phrases.banyanApp('Cumulative Flow'),
        //     labelB: translate.phrases.banyanApp('YTD'),
        //   }),
        //   dataIndex: 'irrigationActivity_YTD_cumulativeFlowMeter_gallons',
        //   key: 'irrigationActivity_YTD_cumulativeFlowMeter_gallons',
        //   width: 200,
        //   render: (value: number) => {
        //     if (value) return `${unitConverter.flowMeter(value).valueWithSuffix()}`

        //     return translate.phrases.templates('-')
        //   },
        // },
        ...(isSouthernHemisphere
          ? [
              {
                title: (
                  <EllipsisTooltip
                    fullText={translate.phrases.templates('{{labelA}} ({{labelB}})', {
                      labelA: translate.phrases.banyanApp('Applied'),
                      labelB: translate.phrases.banyanApp('Since {{date}}', {
                        date: translate.dates.format(moment.tz('UTC').month(3).date(1), 'MMM D'), // Apr 1
                      }),
                    })}
                  />
                ),
                dataIndex: 'irrigationActivity_sinceApr1Sum_equipment_ms',
                key: 'irrigationActivity_sinceApr1Sum_equipment_ms',
                width: 200,
                render: (_: number, record: CurrentValuesDataInfo) => {
                  const apr1SumKey = Object.keys(record).find((key) =>
                    key.startsWith('irrigationActivity_sinceApr1Sum'),
                  )

                  if (!isNil(apr1SumKey)) {
                    return renderAppliedAmount(record, apr1SumKey, currentView, pointsFlowRates)
                  }

                  return translate.phrases.templates('-')
                },
              },
              {
                title: (
                  <EllipsisTooltip
                    fullText={translate.phrases.templates('{{labelA}} ({{labelB}})', {
                      labelA: translate.phrases.banyanApp('Applied'),
                      labelB: translate.phrases.banyanApp('Since {{date}}', {
                        date: translate.dates.format(moment.tz('UTC').month(6).date(1), 'MMM D'), // Jul 1
                      }),
                    })}
                  />
                ),
                dataIndex: 'irrigationActivity_sinceJul1Sum_equipment_ms',
                key: 'irrigationActivity_sinceJul1Sum_equipment_ms',
                width: 200,
                render: (_: number, record: CurrentValuesDataInfo) => {
                  const jul1SumKey = Object.keys(record).find((key) =>
                    key.startsWith('irrigationActivity_sinceJul1Sum'),
                  )

                  if (jul1SumKey) {
                    return renderAppliedAmount(record, jul1SumKey, currentView, pointsFlowRates)
                  }

                  return translate.phrases.templates('-')
                },
              },
            ]
          : []),
        ...(!isSouthernHemisphere
          ? [
              {
                title: (
                  <EllipsisTooltip
                    fullText={translate.phrases.templates('{{labelA}} ({{labelB}})', {
                      labelA: translate.phrases.banyanApp('Applied'),
                      labelB: translate.phrases.banyanApp('Since {{date}}', {
                        date: translate.dates.format(moment.tz('UTC').month(0).date(1), 'MMM D'), // Jan 1
                      }),
                    })}
                  />
                ),
                dataIndex: 'irrigationActivity_sinceJan1Sum_equipment_ms',
                key: 'irrigationActivity_sinceJan1Sum_equipment_ms',
                width: 200,
                render: (_: number, record: CurrentValuesDataInfo) => {
                  const jan1SumKey = Object.keys(record).find((key) =>
                    key.startsWith('irrigationActivity_sinceJan1Sum'),
                  )

                  if (jan1SumKey) {
                    return renderAppliedAmount(record, jan1SumKey, currentView, pointsFlowRates)
                  }

                  return translate.phrases.templates('-')
                },
              },
              {
                title: (
                  <EllipsisTooltip
                    fullText={translate.phrases.templates('{{labelA}} ({{labelB}})', {
                      labelA: translate.phrases.banyanApp('Applied'),
                      labelB: translate.phrases.banyanApp('Since {{date}}', {
                        date: translate.dates.format(moment.tz('UTC').month(9).date(1), 'MMM D'), // Oct 1
                      }),
                    })}
                  />
                ),
                dataIndex: 'irrigationActivity_sinceOct1Sum_equipment_ms',
                key: 'irrigationActivity_sinceOct1Sum_equipment_ms',
                width: 200,
                render: (_: number, record: CurrentValuesDataInfo) => {
                  const oct1Sum = Object.keys(record).find((key) =>
                    key.startsWith('irrigationActivity_sinceOct1Sum'),
                  )

                  if (oct1Sum) {
                    return renderAppliedAmount(record, oct1Sum, currentView, pointsFlowRates)
                  }

                  return translate.phrases.templates('-')
                },
              },
            ]
          : []),
      ],
      plant_stress: [
        getStationsColumn(view),
        {
          title: (
            <EllipsisTooltip
              fullText={translate.phrases.templates('{{labelA}} ({{labelB}})', {
                labelA: translate.phrases.banyanApp('Plant Stress'),
                labelB: translate.phrases.banyanApp('Yesterday'),
              })}
            />
          ),
          dataIndex: 'plantStress_yesterday_signalTS5',
          key: 'plantStress_yesterday_signalTS5',
          width: 170,
          render: (value: number) =>
            !isNil(value) ? unitConverter.plantWaterStress(value).value() : translate.phrases.templates('-'),
        },
      ],
      soil: [
        getStationsColumn(view),
        {
          title: <EllipsisTooltip fullText={translate.phrases.banyanApp('Root Zone Average')} />,
          dataIndex: 'root_zone_average',
          key: 'root_zone_average',
          width: 200,
          render: (value: number) => {
            if (!isNil(value)) {
              const valueToUse = roundToDecimalPlaces(
                value,
                unitConverter.soilAwc().defaultNumberOfDecimalPlaces(),
              )

              const backgroundColor = getSoilAWCBackgroundColor(valueToUse)

              return (
                <BadgeCell backgroundColor={backgroundColor}>
                  {unitConverter.soilAwc(valueToUse).valueWithSuffix()}
                </BadgeCell>
              )
            }

            return translate.phrases.templates('-')
          },
        },
        {
          title: (
            <EllipsisTooltip fullText={translate.phrases.banyanApp('Depth {{index}} AWC', { index: 1 })} />
          ),
          dataIndex: 'soil_awc_1',
          key: 'soil_awc_1',
          width: 150,
          render: (value: number, record: CurrentValuesDataInfo) => {
            if (!isNil(value) && record.fav_depths) {
              const height = parseInt(record.fav_depths[0].replace(/\D/g, ''), 10) * -1

              return `${unitConverter
                .soilAwc(roundToDecimalPlaces(value, unitConverter.soilAwc().defaultNumberOfDecimalPlaces()))
                .valueWithSuffix()} (-${unitConverter.soilDepth(height).valueWithSuffix()})`
            }

            return translate.phrases.templates('-')
          },
        },
        {
          title: (
            <EllipsisTooltip fullText={translate.phrases.banyanApp('Depth {{index}} AWC', { index: 2 })} />
          ),
          dataIndex: 'soil_awc_2',
          key: 'soil_awc_2',
          width: 150,
          render: (value: number, record: CurrentValuesDataInfo) => {
            if (!isNil(value) && record.fav_depths) {
              const height = parseInt(record.fav_depths[1].replace(/\D/g, ''), 10) * -1

              return `${unitConverter
                .soilAwc(roundToDecimalPlaces(value, unitConverter.soilAwc().defaultNumberOfDecimalPlaces()))
                .valueWithSuffix()} (-${unitConverter.soilDepth(height).valueWithSuffix()})`
            }

            return translate.phrases.templates('-')
          },
        },
        {
          title: (
            <EllipsisTooltip fullText={translate.phrases.banyanApp('Depth {{index}} AWC', { index: 3 })} />
          ),
          dataIndex: 'soil_awc_3',
          key: 'soil_awc_3',
          width: 150,
          render: (value: number, record: CurrentValuesDataInfo) => {
            if (!isNil(value) && record.fav_depths) {
              const height = parseInt(record.fav_depths[2].replace(/\D/g, ''), 10) * -1

              return `${unitConverter
                .soilAwc(roundToDecimalPlaces(value, unitConverter.soilAwc().defaultNumberOfDecimalPlaces()))
                .valueWithSuffix()} (-${unitConverter.soilDepth(height).valueWithSuffix()})`
            }

            return translate.phrases.templates('-')
          },
        },
        {
          title: (
            <EllipsisTooltip fullText={translate.phrases.banyanApp('Depth {{index}} AWC', { index: 4 })} />
          ),
          dataIndex: 'soil_awc_4',
          key: 'soil_awc_4',
          width: 150,
          render: (value: number, record: CurrentValuesDataInfo) => {
            if (!isNil(value) && record.fav_depths) {
              const height = parseInt(record.fav_depths[3].replace(/\D/g, ''), 10) * -1

              return `${unitConverter
                .soilAwc(roundToDecimalPlaces(value, unitConverter.soilAwc().defaultNumberOfDecimalPlaces()))
                .valueWithSuffix()} (-${unitConverter.soilDepth(height).valueWithSuffix()})`
            }

            return translate.phrases.templates('-')
          },
        },
      ],
    },
    sprays: {
      spray_conditions: [
        getBlocksColumn(view),
        {
          title: <EllipsisTooltip fullText={translate.phrases.banyanApp('Conditions')} />,
          dataIndex: 'sprayConditions_currentHour',
          key: 'sprayConditions_currentHour',
          width: 150,
          render: (value: ESprayRecommendation | null) => {
            const { color, name } = getSprayConditionsTitleAndColor(value)

            return <BadgeCell backgroundColor={color}>{name}</BadgeCell>
          },
        },
      ],
    },
    pests: {
      ...(insectIds
        ? {
            // Trap catches columns
            trap_catches: [
              {
                title: <EllipsisTooltip fullText={translate.phrases.banyanApp('Pest Type')} />,
                dataIndex: 'pest_type',
                key: 'pest_type',
                width: 200,
                render: (_: number, record: CurrentValuesDataInfo) => {
                  // Get the insect ID from the record's keys
                  const recordKey = Object.keys(record).find(
                    (key) => key.startsWith('insectTrapCatches_') && key.includes('insectId'),
                  )

                  let insectId = -1

                  if (recordKey && recordKey.match(/insectId(\d+)/)?.[1]) {
                    insectId = Number(recordKey.match(/insectId(\d+)/)?.[1])
                  }

                  return getInsectNameFromId(insectId)
                },
                sorter: (a: CurrentValuesDataInfo, b: CurrentValuesDataInfo) => {
                  // Simple string comparison for pest name A-Z
                  const getPestName = (record: CurrentValuesDataInfo) => {
                    const recordKey = Object.keys(record).find(
                      (key) => key.startsWith('insectTrapCatches_') && key.includes('insectId'),
                    )

                    let insectId = -1

                    if (recordKey && recordKey.match(/insectId(\d+)/)?.[1]) {
                      insectId = Number(recordKey.match(/insectId(\d+)/)?.[1])
                    }

                    return getInsectNameFromId(insectId) || ''
                  }

                  return getPestName(a).localeCompare(getPestName(b), undefined, { sensitivity: 'base' })
                },
              },
              {
                title: <EllipsisTooltip fullText={translate.phrases.banyanApp('Today')} />,
                dataIndex: 'insectTrapCatches_todaySum_insectId',
                key: 'insectTrapCatches_todaySum_insectId',
                width: 150,
                render: (_: number, record: CurrentValuesDataInfo) => {
                  const recordKey = Object.keys(record).find(
                    (key) => key.startsWith('insectTrapCatches_') && key.includes('insectId'),
                  )

                  if (recordKey) {
                    const insectId = recordKey.match(/insectId(\d+)/)?.[1]

                    const value =
                      record[`insectTrapCatches_todaySum_insectId${insectId}` as TInsectTrapCatchesKey]

                    return unitConverter.insectTrapCatches(value).valueWithSuffix()
                  }

                  return translate.phrases.templates('-')
                },
                sorter: (a: CurrentValuesDataInfo, b: CurrentValuesDataInfo) => {
                  const prefix = 'insectTrapCatches_todaySum'
                  const pestType = 'insectTrapCatches_'
                  const aVal = pestSorter(a, prefix, pestType)
                  const bVal = pestSorter(b, prefix, pestType)

                  return basicSort(aVal, bVal)
                },
              },
              {
                title: (
                  <EllipsisTooltip
                    fullText={translate.phrases.banyanApp('Last {{count}} Days', { count: 2 })}
                  />
                ),
                dataIndex: 'insectTrapCatches_previous2DaysSum_insectId',
                key: 'insectTrapCatches_previous2DaysSum_insectId',
                width: 150,
                render: (_: number, record: CurrentValuesDataInfo) => {
                  const recordKey = Object.keys(record).find(
                    (key) => key.startsWith('insectTrapCatches_') && key.includes('insectId'),
                  )

                  if (recordKey) {
                    const insectId = recordKey.match(/insectId(\d+)/)?.[1]

                    const value =
                      record[
                        `insectTrapCatches_previous2DaysSum_insectId${insectId}` as TInsectTrapCatchesKey
                      ]

                    return unitConverter.insectTrapCatches(value).valueWithSuffix()
                  }

                  return translate.phrases.templates('-')
                },
                sorter: (a: CurrentValuesDataInfo, b: CurrentValuesDataInfo) => {
                  const prefix = 'insectTrapCatches_previous2DaysSum'
                  const pestType = 'insectTrapCatches_'
                  const aVal = pestSorter(a, prefix, pestType)
                  const bVal = pestSorter(b, prefix, pestType)

                  return basicSort(aVal, bVal)
                },
              },
              {
                title: (
                  <EllipsisTooltip
                    fullText={translate.phrases.banyanApp('Last {{count}} Days', { count: 3 })}
                  />
                ),
                dataIndex: 'insectTrapCatches_previous3DaysSum_insectId',
                key: 'insectTrapCatches_previous3DaysSum_insectId',
                width: 150,
                render: (_: number, record: CurrentValuesDataInfo) => {
                  const recordKey = Object.keys(record).find(
                    (key) => key.startsWith('insectTrapCatches_') && key.includes('insectId'),
                  )

                  if (recordKey) {
                    const insectId = recordKey.match(/insectId(\d+)/)?.[1]

                    const value =
                      record[
                        `insectTrapCatches_previous3DaysSum_insectId${insectId}` as TInsectTrapCatchesKey
                      ]

                    return unitConverter.insectTrapCatches(value).valueWithSuffix()
                  }

                  return translate.phrases.templates('-')
                },
                sorter: (a: CurrentValuesDataInfo, b: CurrentValuesDataInfo) => {
                  const prefix = 'insectTrapCatches_previous3DaysSum'
                  const pestType = 'insectTrapCatches_'
                  const aVal = pestSorter(a, prefix, pestType)
                  const bVal = pestSorter(b, prefix, pestType)

                  return basicSort(aVal, bVal)
                },
              },

              {
                title: (
                  <EllipsisTooltip
                    fullText={translate.phrases.banyanApp('Last {{count}} Days', { count: 7 })}
                  />
                ),
                dataIndex: 'insectTrapCatches_previous7DaysSum_insectId',
                key: 'insectTrapCatches_previous7DaysSum_insectId',
                width: 150,
                render: (_: number, record: CurrentValuesDataInfo) => {
                  const recordKey = Object.keys(record).find(
                    (key) => key.startsWith('insectTrapCatches_') && key.includes('insectId'),
                  )

                  if (recordKey) {
                    const insectId = recordKey.match(/insectId(\d+)/)?.[1]

                    const value =
                      record[
                        `insectTrapCatches_previous7DaysSum_insectId${insectId}` as TInsectTrapCatchesKey
                      ]

                    return unitConverter.insectTrapCatches(value).valueWithSuffix()
                  }

                  return translate.phrases.templates('-')
                },
                sorter: (a: CurrentValuesDataInfo, b: CurrentValuesDataInfo) => {
                  const prefix = 'insectTrapCatches_previous7DaysSum'
                  const pestType = 'insectTrapCatches_'
                  const aVal = pestSorter(a, prefix, pestType)
                  const bVal = pestSorter(b, prefix, pestType)

                  return basicSort(aVal, bVal)
                },
              },
              {
                title: (
                  <EllipsisTooltip
                    fullText={translate.phrases.banyanApp('Last {{count}} Days', { count: 14 })}
                  />
                ),
                dataIndex: 'insectTrapCatches_previous14DaysSum_insectId',
                key: 'insectTrapCatches_previous14DaysSum_insectId',
                width: 150,
                render: (_: number, record: CurrentValuesDataInfo) => {
                  const recordKey = Object.keys(record).find(
                    (key) => key.startsWith('insectTrapCatches_') && key.includes('insectId'),
                  )

                  if (recordKey) {
                    const insectId = recordKey.match(/insectId(\d+)/)?.[1]

                    const value =
                      record[
                        `insectTrapCatches_previous14DaysSum_insectId${insectId}` as TInsectTrapCatchesKey
                      ]

                    return unitConverter.insectTrapCatches(value).valueWithSuffix()
                  }

                  return translate.phrases.templates('-')
                },
                sorter: (a: CurrentValuesDataInfo, b: CurrentValuesDataInfo) => {
                  const prefix = 'insectTrapCatches_previous14DaysSum'
                  const pestType = 'insectTrapCatches_'
                  const aVal = pestSorter(a, prefix, pestType)
                  const bVal = pestSorter(b, prefix, pestType)

                  return basicSort(aVal, bVal)
                },
              },
              {
                title: (
                  <EllipsisTooltip
                    fullText={translate.phrases.banyanApp('Last {{count}} Days', { count: 30 })}
                  />
                ),
                dataIndex: 'insectTrapCatches_previous30DaysSum_insectId',
                key: 'insectTrapCatches_previous30DaysSum_insectId',
                width: 150,
                render: (_: number, record: CurrentValuesDataInfo) => {
                  const recordKey = Object.keys(record).find(
                    (key) => key.startsWith('insectTrapCatches_') && key.includes('insectId'),
                  )

                  if (recordKey) {
                    const insectId = recordKey.match(/insectId(\d+)/)?.[1]

                    const value =
                      record[
                        `insectTrapCatches_previous30DaysSum_insectId${insectId}` as TInsectTrapCatchesKey
                      ]

                    return unitConverter.insectTrapCatches(value).valueWithSuffix()
                  }

                  return translate.phrases.templates('-')
                },
                sorter: (a: CurrentValuesDataInfo, b: CurrentValuesDataInfo) => {
                  const prefix = 'insectTrapCatches_previous30DaysSum'
                  const pestType = 'insectTrapCatches_'
                  const aVal = pestSorter(a, prefix, pestType)
                  const bVal = pestSorter(b, prefix, pestType)

                  return basicSort(aVal, bVal)
                },
              },
              {
                title: (
                  <EllipsisTooltip
                    fullText={translate.phrases.banyanApp('Last {{count}} Days', { count: 60 })}
                  />
                ),
                dataIndex: 'insectTrapCatches_previous60DaysSum_insectId',
                key: 'insectTrapCatches_previous60DaysSum_insectId',
                width: 150,
                render: (_: number, record: CurrentValuesDataInfo) => {
                  const recordKey = Object.keys(record).find(
                    (key) => key.startsWith('insectTrapCatches_') && key.includes('insectId'),
                  )

                  if (recordKey) {
                    const insectId = recordKey.match(/insectId(\d+)/)?.[1]

                    const value =
                      record[
                        `insectTrapCatches_previous60DaysSum_insectId${insectId}` as TInsectTrapCatchesKey
                      ]

                    return unitConverter.insectTrapCatches(value).valueWithSuffix()
                  }

                  return translate.phrases.templates('-')
                },
                sorter: (a: CurrentValuesDataInfo, b: CurrentValuesDataInfo) => {
                  const prefix = 'insectTrapCatches_previous60DaysSum'
                  const pestType = 'insectTrapCatches_'
                  const aVal = pestSorter(a, prefix, pestType)
                  const bVal = pestSorter(b, prefix, pestType)

                  return basicSort(aVal, bVal)
                },
              },
              {
                title: <EllipsisTooltip fullText={translate.phrases.banyanApp('Since Biofix')} />,
                dataIndex: 'insectTrapCatches_sinceBiofixSum_insectId',
                key: 'insectTrapCatches_sinceBiofixSum_insectId',
                width: 150,
                render: (_: number, record: CurrentValuesDataInfo) => {
                  const recordKey = Object.keys(record).find(
                    (key) => key.startsWith('insectTrapCatches_') && key.includes('insectId'),
                  )

                  if (recordKey) {
                    const insectId = recordKey.match(/insectId(\d+)/)?.[1]

                    const value =
                      record[`insectTrapCatches_sinceBiofixSum_insectId${insectId}` as TInsectTrapCatchesKey]

                    return unitConverter.insectTrapCatches(value).valueWithSuffix()
                  }

                  return translate.phrases.templates('-')
                },
                sorter: (a: CurrentValuesDataInfo, b: CurrentValuesDataInfo) => {
                  const prefix = 'insectTrapCatches_sinceBiofixSum'
                  const pestType = 'insectTrapCatches_'
                  const aVal = pestSorter(a, prefix, pestType)
                  const bVal = pestSorter(b, prefix, pestType)

                  return basicSort(aVal, bVal)
                },
              },
              ...(!isSouthernHemisphere
                ? [
                    {
                      title: (
                        <EllipsisTooltip
                          fullText={translate.phrases.banyanApp('Since {{date}}', {
                            date: translate.dates.format(moment.tz('UTC').month(0).date(1), 'MMM D'), // Jan 1
                          })}
                        />
                      ),
                      dataIndex: 'insectTrapCatches_sinceJan1Sum_insectId',
                      key: 'insectTrapCatches_sinceJan1Sum_insectId',
                      width: 150,
                      render: (_: number, record: CurrentValuesDataInfo) => {
                        const recordKey = Object.keys(record).find(
                          (key) => key.startsWith('insectTrapCatches_') && key.includes('insectId'),
                        )

                        if (recordKey) {
                          const insectId = recordKey.match(/insectId(\d+)/)?.[1]

                          const value =
                            record[
                              `insectTrapCatches_sinceJan1Sum_insectId${insectId}` as TInsectTrapCatchesKey
                            ]

                          return unitConverter.insectTrapCatches(value).valueWithSuffix()
                        }

                        return translate.phrases.templates('-')
                      },
                      sorter: (a: CurrentValuesDataInfo, b: CurrentValuesDataInfo) => {
                        const prefix = 'insectTrapCatches_sinceJan1Sum'
                        const pestType = 'insectTrapCatches_'
                        const aVal = pestSorter(a, prefix, pestType)
                        const bVal = pestSorter(b, prefix, pestType)

                        return basicSort(aVal, bVal)
                      },
                    },
                  ]
                : []),
              ...(isSouthernHemisphere
                ? [
                    {
                      title: (
                        <EllipsisTooltip
                          fullText={translate.phrases.banyanApp('Since {{date}}', {
                            date: translate.dates.format(moment.tz('UTC').month(6).date(1), 'MMM D'), // Jul 1
                          })}
                        />
                      ),
                      dataIndex: 'insectTrapCatches_sinceJul1Sum_insectId',
                      key: 'insectTrapCatches_sinceJul1Sum_insectId',
                      width: 150,
                      render: (_: number, record: CurrentValuesDataInfo) => {
                        const recordKey = Object.keys(record).find(
                          (key) => key.startsWith('insectTrapCatches_') && key.includes('insectId'),
                        )

                        if (recordKey) {
                          const insectId = recordKey.match(/insectId(\d+)/)?.[1]

                          const value =
                            record[
                              `insectTrapCatches_sinceJul1Sum_insectId${insectId}` as TInsectTrapCatchesKey
                            ]

                          return unitConverter.insectTrapCatches(value).valueWithSuffix()
                        }

                        return translate.phrases.templates('-')
                      },
                      sorter: (a: CurrentValuesDataInfo, b: CurrentValuesDataInfo) => {
                        const prefix = 'insectTrapCatches_sinceJul1Sum'
                        const pestType = 'insectTrapCatches_'
                        const aVal = pestSorter(a, prefix, pestType)
                        const bVal = pestSorter(b, prefix, pestType)

                        return basicSort(aVal, bVal)
                      },
                    },
                  ]
                : []),
            ],
            // Pest model columns for each insect
            ...insectIds.reduce((acc: Record<string, TColumn[]>, insectId: number) => {
              acc[`pest_${insectId}`] = [
                getBlocksColumn(view),
                {
                  title: <EllipsisTooltip fullText={translate.phrases.banyanApp('Crop')} />,
                  dataIndex: 'crop',
                  key: 'crop',
                  width: 150,
                  render: (value: string, record: CurrentValuesDataInfo) => {
                    if (currentView?.pest === 'CROP' && record.type === 'summary') {
                      return record.crop || '-'
                    }

                    return value || '-'
                  },
                },
                {
                  title: <EllipsisTooltip fullText={translate.phrases.banyanApp('Biofix Date')} />,
                  dataIndex: 'biofix',
                  key: 'biofix',
                  width: 150,
                  render: (value: string, record: CurrentValuesDataInfo) => {
                    const timezone = record.timezone

                    if (value) {
                      return translate.dates.format(moment.tz(value, timezone), 'MMM D, YYYY')
                    }

                    return translate.phrases.templates('-')
                  },
                  sorter: (a: CurrentValuesDataInfo, b: CurrentValuesDataInfo) => {
                    if (a.biofix && b.biofix) {
                      return +new Date(a.biofix) - +new Date(b.biofix)
                    } else return -1
                  },
                },
                {
                  title: <EllipsisTooltip fullText={translate.phrases.banyanApp('Current')} />,
                  dataIndex: 'insectDegreeDays_sinceBiofixSumMinMedianMax_insectId',
                  key: 'insectDegreeDays_sinceBiofixSumMinMedianMax_insectId',
                  width: 200,
                  render: (_: number, record: CurrentValuesDataInfo) => {
                    if (
                      currentView?.pest === 'CROP' &&
                      record.type === 'summary' &&
                      record.insectId === String(insectId)
                    ) {
                      const { low = null, high = null } = record.currentDD ?? { low: null, high: null }

                      if (low === null && high === null) return translate.phrases.templates('-')

                      return translate.phrases.templates('{{valueMin}} - {{valueMax}} {{unitSymbol}}', {
                        valueMin: unitConverter
                          .insectDegreeDays(low, {
                            decimalPlaces: 1,
                          })
                          .value(),
                        valueMax: unitConverter
                          .insectDegreeDays(high, {
                            decimalPlaces: 1,
                          })
                          .value(),
                        unitSymbol: unitConverter.insectDegreeDays().suffix(),
                      })
                    } else if (currentView?.pest === 'BLOCK') {
                      const ddCurrent = Object.keys(record).find(
                        (key) =>
                          key ===
                          `insectDegreeDays_sinceBiofixSumMinMedianMax_insectId${insectId}_degreeDaysC`,
                      )

                      return !isNil(ddCurrent)
                        ? unitConverter
                            .insectDegreeDays(Number(record[ddCurrent as keyof CurrentValuesDataInfo]), {
                              decimalPlaces: 1,
                            })
                            .valueWithSuffix()
                        : translate.phrases.templates('-')
                    }

                    return translate.phrases.templates('-')
                  },
                  sorter: (a: CurrentValuesDataInfo, b: CurrentValuesDataInfo) => {
                    const prefix = 'insectDegreeDays_sinceBiofixSumMinMedianMax'
                    const pestType = 'insectDegreeDays_'
                    const aVal = pestSorter(a, prefix, pestType, currentView)
                    const bVal = pestSorter(b, prefix, pestType, currentView)

                    return basicSort(aVal, bVal)
                  },
                },
                {
                  title: (
                    <EllipsisTooltip
                      fullText={translate.phrases.banyanApp('{{count}} Days From Now', { count: 7 })}
                    />
                  ),
                  dataIndex: 'insectDegreeDays7DaysFromNow_sinceBiofixSumMinMedianMax_insectId',
                  key: 'insectDegreeDays7DaysFromNow_sinceBiofixSumMinMedianMax_insectId',
                  width: 200,
                  render: (_: number, record: CurrentValuesDataInfo) => {
                    if (
                      currentView?.pest === 'CROP' &&
                      record.type === 'summary' &&
                      record.insectId === String(insectId)
                    ) {
                      const { low = null, high = null } = record['7dayDD'] ?? { low: null, high: null }

                      if (low === null && high === null) return translate.phrases.templates('-')

                      return translate.phrases.templates('{{valueMin}} - {{valueMax}} {{unitSymbol}}', {
                        valueMin: unitConverter
                          .insectDegreeDays(low, {
                            decimalPlaces: 1,
                          })
                          .value(),
                        valueMax: unitConverter
                          .insectDegreeDays(high, {
                            decimalPlaces: 1,
                          })
                          .value(),
                        unitSymbol: unitConverter.insectDegreeDays().suffix(),
                      })
                    } else if (currentView?.pest === 'BLOCK') {
                      const dd7Days = Object.keys(record).find(
                        (key) =>
                          key ===
                          `insectDegreeDays7DaysFromNow_sinceBiofixSumMinMedianMax_insectId${insectId}_degreeDaysC`,
                      )

                      return !isNil(dd7Days)
                        ? unitConverter
                            .insectDegreeDays(Number(record[dd7Days as keyof CurrentValuesDataInfo]), {
                              decimalPlaces: 1,
                            })
                            .valueWithSuffix()
                        : translate.phrases.templates('-')
                    }

                    return translate.phrases.templates('-')
                  },
                  sorter: (a: CurrentValuesDataInfo, b: CurrentValuesDataInfo) => {
                    const prefix = 'insectDegreeDays7DaysFromNow_sinceBiofixSumMinMedianMax'
                    const pestType = 'insectDegreeDays_'
                    const aVal = pestSorter(a, prefix, pestType, currentView)
                    const bVal = pestSorter(b, prefix, pestType, currentView)

                    return basicSort(aVal, bVal)
                  },
                },
                {
                  title: (
                    <EllipsisTooltip
                      fullText={translate.phrases.banyanApp('{{count}} Days From Now', { count: 14 })}
                    />
                  ),
                  dataIndex: 'insectDegreeDays14DaysFromNow_sinceBiofixSumMinMedianMax_insectId',
                  key: 'insectDegreeDays14DaysFromNow_sinceBiofixSumMinMedianMax_insectId',
                  width: 200,
                  render: (_: number, record: CurrentValuesDataInfo) => {
                    if (
                      currentView?.pest === 'CROP' &&
                      record.type === 'summary' &&
                      record.insectId === String(insectId)
                    ) {
                      const { low = null, high = null } = record['14dayDD'] ?? { low: null, high: null }

                      if (low === null && high === null) return translate.phrases.templates('-')

                      return translate.phrases.templates('{{valueMin}} - {{valueMax}} {{unitSymbol}}', {
                        valueMin: unitConverter
                          .insectDegreeDays(low, {
                            decimalPlaces: 1,
                          })
                          .value(),
                        valueMax: unitConverter
                          .insectDegreeDays(high, {
                            decimalPlaces: 1,
                          })
                          .value(),
                        unitSymbol: unitConverter.insectDegreeDays().suffix(),
                      })
                    } else if (currentView?.pest === 'BLOCK') {
                      const dd14Days = Object.keys(record).find(
                        (key) =>
                          key ===
                          `insectDegreeDays14DaysFromNow_sinceBiofixSumMinMedianMax_insectId${insectId}_degreeDaysC`,
                      )

                      return !isNil(dd14Days)
                        ? unitConverter
                            .insectDegreeDays(Number(record[dd14Days as keyof CurrentValuesDataInfo]), {
                              decimalPlaces: 1,
                            })
                            .valueWithSuffix()
                        : translate.phrases.templates('-')
                    }

                    return translate.phrases.templates('-')
                  },
                  sorter: (a: CurrentValuesDataInfo, b: CurrentValuesDataInfo) => {
                    const prefix = 'insectDegreeDays14DaysFromNow_sinceBiofixSumMinMedianMax'
                    const pestType = 'insectDegreeDays_'
                    const aVal = pestSorter(a, prefix, pestType, currentView)
                    const bVal = pestSorter(b, prefix, pestType, currentView)

                    return basicSort(aVal, bVal)
                  },
                },
                ...(insectId === 6 && currentView?.pest === 'BLOCK'
                  ? [
                      {
                        title: (
                          <EllipsisTooltip
                            fullText={translate.phrases.templates('{{labelA}} ({{labelB}})', {
                              labelA: translate.phrases.banyanApp('Almond Hull Split'),
                              labelB: translate.phrases.banyanApp('Nonpareil'),
                            })}
                          />
                        ),
                        dataIndex: 'almondHullSplit_date',
                        key: 'almondHullSplit_date',
                        width: 275,
                        render: (value: Record<string, string>) => {
                          if (value) {
                            return unitConverter.almondHullSplit(value[72]).valueWithSuffix()
                          }

                          return translate.phrases.templates('-')
                        },
                        sorter: (a: CurrentValuesDataInfo, b: CurrentValuesDataInfo) => {
                          if (a.almondHullSplit_date && b.almondHullSplit_date) {
                            return (
                              +new Date(a.almondHullSplit_date[72]) - +new Date(b.almondHullSplit_date[72])
                            )
                          } else return -1
                        },
                      },
                    ]
                  : []),
              ]

              return acc
            }, {}),
          }
        : {}),
    },
    cropDevelopment: {
      almond_bloom: [
        getBlocksColumn(view),
        {
          title: (
            <EllipsisTooltip
              fullText={translate.phrases.templates('{{labelA}} {{labelB}} ({{labelC}})', {
                labelA: translate.phrases.banyanApp('Almond Bloom'),
                labelB: translate.phrases.banyanApp('Today'),
                labelC: cultivar,
              })}
            />
          ),
          dataIndex: 'almondBloom_today_pct',
          key: 'almondBloom_today_pct',
          width: 325,
          render: (value: number) =>
            !isNil(value) && value !== 0
              ? unitConverter.almondBloom(value).valueWithSuffix()
              : translate.phrases.templates('-'),
        },
        {
          title: (
            <EllipsisTooltip
              fullText={translate.phrases.templates('{{labelA}} {{labelB}} ({{labelC}})', {
                labelA: translate.phrases.banyanApp('Almond Bloom'),
                labelB: translate.phrases.banyanApp('Forecast {{count}} Days', { count: 7 }),
                labelC: cultivar,
              })}
            />
          ),
          dataIndex: 'almondBloom_next7Days_pct',
          key: 'almondBloom_next7Days_pct',
          width: 350,
          render: (value: number) =>
            !isNil(value) && value !== 0
              ? unitConverter.almondBloom(value).valueWithSuffix()
              : translate.phrases.templates('-'),
        },
        {
          title: (
            <EllipsisTooltip
              fullText={translate.phrases.templates('{{labelA}} {{labelB}} ({{labelC}})', {
                labelA: translate.phrases.banyanApp('Almond Bloom'),
                labelB: translate.phrases.banyanApp('Forecast {{count}} Days', { count: 14 }),
                labelC: cultivar,
              })}
            />
          ),
          dataIndex: 'almondBloom_next14Days_pct',
          key: 'almondBloom_next14Days_pct',
          width: 350,
          render: (value: number) =>
            !isNil(value) && value !== 0
              ? unitConverter.almondBloom(value).valueWithSuffix()
              : translate.phrases.templates('-'),
        },
      ],
      almond_hull_split: [
        getBlocksColumn(view),
        {
          title: (
            <EllipsisTooltip
              fullText={translate.phrases.templates('{{labelA}} ({{labelB}})', {
                labelA: translate.phrases.banyanApp('Almond Hull Split'),
                labelB: translate.phrases.banyanApp('Nonpareil'),
              })}
            />
          ),
          dataIndex: 'almondHullSplit_date',
          key: 'almondHullSplit_date',
          width: 275,
          render: (value: Record<string, string>) => {
            if (value) {
              return unitConverter.almondHullSplit(value[72]).valueWithSuffix()
            }

            return translate.phrases.templates('-')
          },
          sorter: (a: CurrentValuesDataInfo, b: CurrentValuesDataInfo) => {
            if (a.almondHullSplit_date && b.almondHullSplit_date) {
              return +new Date(a.almondHullSplit_date[72]) - +new Date(b.almondHullSplit_date[72])
            } else return -1
          },
        },
      ],
      bee_hours: [
        getBlocksColumn(view),
        {
          title: (
            <EllipsisTooltip
              fullText={translate.phrases.templates('{{labelA}} ({{labelB}})', {
                labelA: translate.phrases.banyanApp('Bee Hours'),
                labelB: translate.phrases.banyanApp('Last {{count}} Hours', { count: 24 }),
              })}
            />
          ),
          dataIndex: 'beeHours_last24hoursSum_hours',
          key: 'beeHours_last24hoursSum_hours',
          width: 225,
          render: (value: number) =>
            isNil(value) ? translate.phrases.templates('-') : unitConverter.beeHours(value).valueWithSuffix(),
        },
        {
          title: (
            <EllipsisTooltip
              fullText={translate.phrases.templates('{{labelA}} ({{labelB}})', {
                labelA: translate.phrases.banyanApp('Bee Hours'),
                labelB: translate.phrases.banyanApp('Last {{count}} Days', { count: 7 }),
              })}
            />
          ),
          dataIndex: 'beeHours_last7daysSum_hours',
          key: 'beeHours_last7daysSum_hours',
          width: 225,
          render: (value: number) =>
            isNil(value) ? translate.phrases.templates('-') : unitConverter.beeHours(value).valueWithSuffix(),
        },
        {
          title: (
            <EllipsisTooltip
              fullText={translate.phrases.templates('{{labelA}} ({{labelB}})', {
                labelA: translate.phrases.banyanApp('Bee Hours'),
                labelB: translate.phrases.banyanApp('Last {{count}} Days', { count: 30 }),
              })}
            />
          ),
          dataIndex: 'beeHours_last30daysSum_hours',
          key: 'beeHours_last30daysSum_hours',
          width: 225,
          render: (value: number) =>
            isNil(value) ? translate.phrases.templates('-') : unitConverter.beeHours(value).valueWithSuffix(),
        },
        {
          title: (
            <EllipsisTooltip
              fullText={translate.phrases.templates('{{labelA}} ({{labelB}})', {
                labelA: translate.phrases.banyanApp('Bee Hours'),
                labelB: translate.phrases.banyanApp('Next {{count}} Days', { count: 7 }),
              })}
            />
          ),
          dataIndex: 'beeHours_next7daysSum_hours',
          key: 'beeHours_next7daysSum_hours',
          width: 225,
          render: (value: number) =>
            isNil(value) ? translate.phrases.templates('-') : unitConverter.beeHours(value).valueWithSuffix(),
        },
        {
          title: (
            <EllipsisTooltip
              fullText={translate.phrases.templates('{{labelA}} ({{labelB}})', {
                labelA: translate.phrases.banyanApp('Bee Hours'),
                labelB: translate.phrases.banyanApp('Next {{count}} Days', { count: 14 }),
              })}
            />
          ),
          dataIndex: 'beeHours_next14daysSum_hours',
          key: 'beeHours_next14daysSum_hours',
          width: 225,
          render: (value: number) =>
            isNil(value) ? translate.phrases.templates('-') : unitConverter.beeHours(value).valueWithSuffix(),
        },
      ],
      chill: [
        getBlocksColumn(view),
        ...(isSouthernHemisphere
          ? [
              {
                title: (
                  <EllipsisTooltip
                    fullText={unitConverter.chillCumulativeHoursBelow45FMAR().titleWithoutUnit()}
                  />
                ),
                dataIndex: 'chillHoursLessThan45_sinceMar1SumMinMedianMax_hours',
                key: 'chillHoursLessThan45_sinceMar1Sum_hours',
                width: 220,
                render: (value: number) => {
                  if (!isNil(value))
                    return unitConverter.chillCumulativeHoursBelow45FSEP(value).valueWithSuffix()

                  return translate.phrases.templates('-')
                },
              },
              {
                title: (
                  <EllipsisTooltip
                    fullText={unitConverter.chillCumulativeHoursBetween32and45FMAR().titleWithoutUnit()}
                  />
                ),
                dataIndex: 'chillHoursBetween32And45_sinceMar1SumMinMedianMax_hours',
                key: 'chillHoursBetween32And45_sinceMar1Sum_hours',
                width: 220,
                render: (value: number) => {
                  if (!isNil(value))
                    return unitConverter.chillCumulativeHoursBetween32and45FSEP(value).valueWithSuffix()

                  return translate.phrases.templates('-')
                },
              },
              {
                title: (
                  <EllipsisTooltip fullText={unitConverter.chillCumulativePortionsMAR().titleWithoutUnit()} />
                ),
                dataIndex: 'chillPortions_sinceMar1SumMinMedianMax_portions',
                key: 'chillPortions_sinceMar1Sum_portions',
                width: 200,
                render: (value: number) => {
                  if (!isNil(value)) return unitConverter.chillCumulativePortionsSEP(value).valueWithSuffix()

                  return translate.phrases.templates('-')
                },
              },
              {
                title: (
                  <EllipsisTooltip
                    fullText={unitConverter.chillCumulativeHoursBelow45FMAY().titleWithoutUnit()}
                  />
                ),
                dataIndex: 'chillHoursLessThan45_sinceMay1SumMinMedianMax_hours',
                key: 'chillHoursLessThan45_sinceMay1Sum_hours',
                width: 200,
                render: (value: number) => {
                  if (!isNil(value))
                    return unitConverter.chillCumulativeHoursBelow45FSEP(value).valueWithSuffix()

                  return translate.phrases.templates('-')
                },
              },
              {
                title: (
                  <EllipsisTooltip
                    fullText={unitConverter.chillCumulativeHoursBetween32and45FMAY().titleWithoutUnit()}
                  />
                ),
                dataIndex: 'chillHoursBetween32And45_sinceMay1SumMinMedianMax_hours',
                key: 'chillHoursBetween32And45_sinceMay1Sum_hours',
                width: 210,
                render: (value: number) => {
                  if (!isNil(value))
                    return unitConverter.chillCumulativeHoursBetween32and45FSEP(value).valueWithSuffix()

                  return translate.phrases.templates('-')
                },
              },
              {
                title: (
                  <EllipsisTooltip fullText={unitConverter.chillCumulativePortionsMAY().titleWithoutUnit()} />
                ),
                dataIndex: 'chillPortions_sinceMay1SumMinMedianMax_portions',
                key: 'chillPortions_sinceMay1Sum_portions',
                width: 180,
                render: (value: number) => {
                  if (!isNil(value)) return unitConverter.chillCumulativePortionsSEP(value).valueWithSuffix()

                  return translate.phrases.templates('-')
                },
              },
            ]
          : []),
        ...(!isSouthernHemisphere
          ? [
              {
                title: (
                  <EllipsisTooltip
                    fullText={unitConverter.chillCumulativeHoursBelow45FSEP().titleWithoutUnit()}
                  />
                ),
                dataIndex: 'chillHoursLessThan45_sinceSep1SumMinMedianMax_hours',
                key: 'chillHoursLessThan45_sinceSep1Sum_hours',
                width: 255,
                render: (value: number) => {
                  if (!isNil(value))
                    return unitConverter.chillCumulativeHoursBelow45FSEP(value).valueWithSuffix()

                  return translate.phrases.templates('-')
                },
              },
              {
                title: (
                  <EllipsisTooltip
                    fullText={unitConverter.chillCumulativeHoursBetween32and45FSEP().titleWithoutUnit()}
                  />
                ),
                dataIndex: 'chillHoursBetween32And45_sinceSep1SumMinMedianMax_hours',
                key: 'chillHoursBetween32And45_sinceSep1Sum_hours',
                width: 255,
                render: (value: number) => {
                  if (!isNil(value))
                    return unitConverter.chillCumulativeHoursBetween32and45FSEP(value).valueWithSuffix()

                  return translate.phrases.templates('-')
                },
              },
              {
                title: (
                  <EllipsisTooltip fullText={unitConverter.chillCumulativePortionsSEP().titleWithoutUnit()} />
                ),
                dataIndex: 'chillPortions_sinceSep1SumMinMedianMax_portions',
                key: 'chillPortions_sinceSep1Sum_portions',
                width: 210,
                render: (value: number) => {
                  if (!isNil(value)) return unitConverter.chillCumulativePortionsSEP(value).valueWithSuffix()

                  return translate.phrases.templates('-')
                },
              },
              {
                title: (
                  <EllipsisTooltip
                    fullText={unitConverter.chillCumulativeHoursBelow45FNOV().titleWithoutUnit()}
                  />
                ),
                dataIndex: 'chillHoursLessThan45_sinceNov1SumMinMedianMax_hours',
                key: 'chillHoursLessThan45_sinceNov1Sum_hours',
                width: 255,
                render: (value: number) => {
                  if (!isNil(value))
                    return unitConverter.chillCumulativeHoursBelow45FNOV(value).valueWithSuffix()

                  return translate.phrases.templates('-')
                },
              },
              {
                title: (
                  <EllipsisTooltip
                    fullText={unitConverter.chillCumulativeHoursBetween32and45FNOV().titleWithoutUnit()}
                  />
                ),
                dataIndex: 'chillHoursBetween32And45_sinceNov1SumMinMedianMax_hours',
                key: 'chillHoursBetween32And45_sinceNov1Sum_hours',
                width: 255,
                render: (value: number) => {
                  if (!isNil(value))
                    return unitConverter.chillCumulativeHoursBetween32and45FNOV(value).valueWithSuffix()

                  return translate.phrases.templates('-')
                },
              },
              {
                title: (
                  <EllipsisTooltip fullText={unitConverter.chillCumulativePortionsNOV().titleWithoutUnit()} />
                ),
                dataIndex: 'chillPortions_sinceNov1SumMinMedianMax_portions',
                key: 'chillPortions_sinceNov1Sum_portions',
                width: 210,
                render: (value: number) => {
                  if (!isNil(value)) return unitConverter.chillCumulativePortionsSEP(value).valueWithSuffix()

                  return translate.phrases.templates('-')
                },
              },
            ]
          : []),
      ],
    },
    // equipment: {
    //   wind_machine: [
    //     {
    //       title:  <EllipsisTooltip fullText={ translate.phrases.banyanApp('Temperature')} />,
    //       dataIndex: 'windMachine_temperature_c',
    //       key: 'windMachine_temperature_c',
    //       width: 150,
    //       render: (value: number) =>
    //         unitConverter.windMachineTemperature(value).valueWithSuffix(),
    //     },
    //     {
    //       title:  <EllipsisTooltip fullText={ translate.phrases.banyanApp('Status')} />,
    //       dataIndex: 'windMachine_status',
    //       key: 'windMachine_status',
    //       width: 150,
    //       render: (value: string) => {
    //         const statusValue = value as unknown as string
    //         const { color, name, textColor } = getWindMachineActivityTitleAndColor(statusValue)

    //         return (
    //           <BadgeCell color={textColor} backgroundColor={color}>
    //             {name.toUpperCase()}
    //           </BadgeCell>
    //         )
    //       },
    //     },
    //     {
    //       title:  <EllipsisTooltip fullText={ translate.phrases.banyanApp('Voltage')} />,
    //       dataIndex: 'windMachine_batteryVoltage_v',
    //       key: 'windMachine_batteryVoltage_v',
    //       width: 150,
    //       render: (value: number) => {
    //         const WMVoltageValueStr = unitConverter
    //           .windMachineVoltage(value)
    //           .valueWithSuffix()

    //         let presumedState = 'ON'

    //         const voltage = WMVoltageValueStr.match(/[\d.]+/)
    //         const WMVoltageValue = voltage ? parseFloat(voltage[0]) : null

    //         if (isNil(WMVoltageValue) || WMVoltageValue < 11.8) {
    //           presumedState = 'ERROR'
    //         }

    //         const { color, textColor } = getWindMachineActivityTitleAndColor(presumedState)

    //         return (
    //           <BadgeCell color={textColor} backgroundColor={color}>
    //             {WMVoltageValueStr}
    //           </BadgeCell>
    //         )
    //       },
    //     },
    //     {
    //       title:  <EllipsisTooltip fullText={ translate.phrases.banyanApp('RPM')} />,
    //       dataIndex: 'windMachine_rpm',
    //       key: 'windMachine_rpm',
    //       width: 150,
    //       render: (value: number) => {
    //         const WMRPMValue = unitConverter.windMachineRPM(value).value() || 0

    //         let presumedState = 'OFF'

    //         if (WMRPMValue >= 350) {
    //           presumedState = 'ON'
    //         } else if (WMRPMValue >= 150) {
    //           presumedState = 'WARM UP'
    //         }

    //         const { color, textColor } = getWindMachineActivityTitleAndColor(presumedState)

    //         return (
    //           <BadgeCell color={textColor} backgroundColor={color}>
    //             {presumedState}
    //           </BadgeCell>
    //         )
    //       },
    //     },
    //   ],
    // },
    diseases: {
      alternaria: [
        getBlocksColumn(view),
        {
          title: (
            <EllipsisTooltip
              fullText={translate.phrases.templates('{{labelA}} ({{labelB}})', {
                labelA: translate.phrases.banyanApp('7 Day DSV'),
                labelB: translate.phrases.banyanApp('Yesterday'),
              })}
            />
          ),
          dataIndex: 'alternariaCumulativeRolling7Days_yesterday_dsv',
          key: 'alternariaCumulativeRolling7Days_yesterday_dsv',
          width: 200,
          render: (value: number) =>
            isNil(value)
              ? translate.phrases.templates('-')
              : unitConverter.alternaria(value).valueWithSuffix(),
        },
        {
          title: (
            <EllipsisTooltip
              fullText={translate.phrases.templates('{{labelA}} ({{labelB}})', {
                labelA: translate.phrases.banyanApp('7 Day DSV'),
                labelB: translate.phrases.banyanApp('Today'),
              })}
            />
          ),
          dataIndex: 'alternariaCumulativeRolling7Days_today_dsv',
          key: 'alternariaCumulativeRolling7Days_today_dsv',
          width: 200,
          render: (value: number) =>
            isNil(value)
              ? translate.phrases.templates('-')
              : unitConverter.alternaria(value).valueWithSuffix(),
        },

        {
          title: (
            <EllipsisTooltip
              fullText={translate.phrases.templates('{{labelA}} ({{labelB}})', {
                labelA: translate.phrases.banyanApp('7 Day DSV'),
                labelB: translate.phrases.banyanApp('Tomorrow'),
              })}
            />
          ),
          dataIndex: 'alternariaCumulativeRolling7Days_tomorrow_dsv',
          key: 'alternariaCumulativeRolling7Days_tomorrow_dsv',
          width: 200,
          render: (value: number) =>
            isNil(value)
              ? translate.phrases.templates('-')
              : unitConverter.alternaria(value).valueWithSuffix(),
        },
        {
          title: (
            <EllipsisTooltip
              fullText={translate.phrases.templates('{{labelA}} ({{labelB}})', {
                labelA: translate.phrases.banyanApp('Max 7 Day DSV'),
                labelB: translate.phrases.banyanApp('Next {{count}} Days', { count: 7 }),
              })}
            />
          ),
          dataIndex: 'alternariaCumulativeRolling7Days_next7DaysMax_dsv',
          key: 'alternariaCumulativeRolling7Days_next7DaysMax_dsv',
          width: 250,
          render: (value: number) =>
            isNil(value)
              ? translate.phrases.templates('-')
              : unitConverter.alternaria(value).valueWithSuffix(),
        },
        {
          title: (
            <EllipsisTooltip
              fullText={translate.phrases.templates('{{labelA}} ({{labelB}})', {
                labelA: translate.phrases.banyanApp('Max 7 Day DSV'),
                labelB: translate.phrases.banyanApp('Next {{count}} Days', { count: 14 }),
              })}
            />
          ),
          dataIndex: 'alternariaCumulativeRolling7Days_next14DaysMax_dsv',
          key: 'alternariaCumulativeRolling7Days_next14DaysMax_dsv',
          width: 250,
          render: (value: number) =>
            isNil(value)
              ? translate.phrases.templates('-')
              : unitConverter.alternaria(value).valueWithSuffix(),
        },
      ],
      fire_blight: [
        getBlocksColumn(view),
        {
          title: (
            <EllipsisTooltip
              fullText={translate.phrases.templates('{{labelA}} ({{labelB}})', {
                labelA: translate.phrases.banyanApp('Max TRV'),
                labelB: translate.phrases.banyanApp('Yesterday'),
              })}
            />
          ),
          dataIndex: 'fireBlight_yesterday_trv',
          key: 'fireBlight_yesterday_trv',
          width: 200,
          render: (value: number) =>
            isNil(value)
              ? translate.phrases.templates('-')
              : unitConverter.fireBlight(value).valueWithSuffix(),
        },
        {
          title: (
            <EllipsisTooltip
              fullText={translate.phrases.templates('{{labelA}} ({{labelB}})', {
                labelA: translate.phrases.banyanApp('TRV'),
                labelB: translate.phrases.banyanApp('Current'),
              })}
            />
          ),
          dataIndex: 'fireBlight_today_trv',
          key: 'fireBlight_today_trv',
          width: 200,
          render: (value: number) =>
            isNil(value)
              ? translate.phrases.templates('-')
              : unitConverter.fireBlight(value).valueWithSuffix(),
        },
        {
          title: (
            <EllipsisTooltip
              fullText={translate.phrases.templates('{{labelA}} ({{labelB}})', {
                labelA: translate.phrases.banyanApp('Max TRV'),
                labelB: translate.phrases.banyanApp('Tomorrow'),
              })}
            />
          ),
          dataIndex: 'fireBlight_tomorrow_trv',
          key: 'fireBlight_tomorrow_trv',
          width: 200,
          render: (value: number) =>
            isNil(value)
              ? translate.phrases.templates('-')
              : unitConverter.fireBlight(value).valueWithSuffix(),
        },
        {
          title: (
            <EllipsisTooltip
              fullText={translate.phrases.templates('{{labelA}} ({{labelB}})', {
                labelA: translate.phrases.banyanApp('Max TRV'),
                labelB: translate.phrases.banyanApp('Next {{count}} Days', { count: 7 }),
              })}
            />
          ),
          dataIndex: 'fireBlight_next7DaysMax_trv',
          key: 'fireBlight_next7DaysMax_trv',
          width: 250,
          render: (value: number) =>
            isNil(value)
              ? translate.phrases.templates('-')
              : unitConverter.fireBlight(value).valueWithSuffix(),
        },
        {
          title: (
            <EllipsisTooltip
              fullText={translate.phrases.templates('{{labelA}} ({{labelB}})', {
                labelA: translate.phrases.banyanApp('Max TRV'),
                labelB: translate.phrases.banyanApp('Next {{count}} Days', { count: 14 }),
              })}
            />
          ),
          dataIndex: 'fireBlight_next14DaysMax_trv',
          key: 'fireBlight_next14DaysMax_trv',
          width: 250,
          render: (value: number) =>
            isNil(value)
              ? translate.phrases.templates('-')
              : unitConverter.fireBlight(value).valueWithSuffix(),
        },
      ],
      // cherry_powdery_mildew: [
      //   {
      //     title:  <EllipsisTooltip fullText={ translate.phrases.templates('{{labelA}} ({{labelB}})', {
      //       labelA: 'Predicted Risk',
      //       labelB: translate.phrases.banyanApp('Today'),
      //     })} />,
      //     dataIndex: 'cherryPowderyMildewModelPredictedRisk_today',
      //     key: 'cherryPowderyMildewModelPredictedRisk_today',
      //     width: 200,
      //     render: (value: number) =>
      //       isNil(value)
      //         ? translate.phrases.templates('-')
      //         : unitConverter.cherryPowderyMildew(value).valueWithSuffix(),
      //   },
      // ],
      // grape_powdery_mildew: [
      //   {
      //     title:  <EllipsisTooltip fullText={ translate.phrases.templates('{{labelA}} ({{labelB}})', {
      //       labelA: 'Predicted Risk',
      //       labelB: translate.phrases.banyanApp('Today'),
      //     })} />,
      //     dataIndex: 'grapePowderyMildewModelPredictedRisk_today',
      //     key: 'grapePowderyMildewModelPredictedRisk_today',
      //     width: 200,
      //     render: (value: number) =>
      //       isNil(value)
      //         ? translate.phrases.templates('-')
      //         : unitConverter.grapePowderyMildew(value).valueWithSuffix(),
      //   },
      // ],
    },
  }

  const leadingCols = ['blockName', 'pointName']

  let needLeadingCols = {
    blockName: false,
    pointName: false,
  }

  let dataColumns: TColumn[] = []

  const hasAnyActiveIA = groupedKeys.irrigation.some(
    (key) =>
      Boolean(selectedValueGroups[key as keyof typeof selectedValueGroups]) &&
      selectedValueGroups[key as keyof typeof selectedValueGroups] !== false,
  )

  let modifiedSelectedValueGroups = { ...selectedValueGroups }

  if (hasAnyActiveIA) {
    // eslint-disable-next-line padding-line-between-statements
    ;(groupedKeys.irrigation as Array<keyof typeof modifiedSelectedValueGroups>).forEach((key) => {
      delete modifiedSelectedValueGroups[key]
    })

    modifiedSelectedValueGroups['irrigation_activity' as keyof typeof modifiedSelectedValueGroups] =
      +new Date()
  }

  selectedValueGroupsArray?.forEach((selectedValueGroup: TValueGroupLabel) => {
    if (
      !view ||
      modifiedSelectedValueGroups[selectedValueGroup?.value as keyof typeof selectedValueGroups] === false
    )
      return

    let groupCols: TColumn[] = columns[view]?.[selectedValueGroup?.value] ?? []

    // --- Pest-specific handling for blocks column ---
    groupCols.forEach((col) => {
      if (col.dataIndex === 'blockName') {
        if (
          view === 'pests'
            ? currentView?.pest === 'BLOCK' // show "Blocks" only if pest view is BLOCK
            : true // all other views: show if present
        ) {
          needLeadingCols.blockName = true
        }
      }

      if (col.dataIndex === 'pointName') needLeadingCols.pointName = true
    })

    groupCols = groupCols.filter((col) => {
      // Always filter out leading "blockName"/"pointName"
      if (leadingCols.includes(col.dataIndex)) {
        // Special case: in pest view, block column only in BLOCK mode
        if (view === 'pests' && col.dataIndex === 'blockName' && currentView?.pest !== 'BLOCK') {
          return false
        }

        // Otherwise, always remove from groupCols (will be prepended if needed)
        return false
      }

      return true
    })

    dataColumns.push(...groupCols)
  })

  // Always add blocks/point columns in this order if needed
  let finalColumns: TColumn[] = []

  if (needLeadingCols.blockName) finalColumns.push(getBlocksColumn(view))

  if (needLeadingCols.pointName) finalColumns.push(getStationsColumn(view))

  finalColumns.push(...dataColumns)

  if (timestamp) {
    finalColumns.push(getTimestampColumn(timestamp))
  }

  return finalColumns
}
